<template>
  <div class="content-explorer">
    <div class="options-area">
      <div v-if="enablePileRef" class="btn-group" role="group" aria-label="Layout">
        <input type="radio" class="btn-check" name="layout" id="layoutPile" autocomplete="off" :value="LayoutType.Pile" v-model="selectedLayoutRef"/>
        <label class="btn btn-outline-secondary btn-sm" for="layoutPile">
          <img src="@/assets/icons/pile_60.png"/>
        </label>

        <input type="radio" class="btn-check" name="layout" id="layoutGrid" autocomplete="off" :value="LayoutType.Grid" v-model="selectedLayoutRef"/>
        <label class="btn btn-outline-secondary btn-sm" for="layoutGrid">
          <img src="@/assets/icons/grid_60.png"/>
        </label>
      </div>
      <div v-if="false" class="btn-group" role="group" aria-label="Filters">
        <input type="radio" class="btn-check" name="filters" id="all" autocomplete="off" :value="undefined" v-model="selectedFilterRef"/>
        <label class="btn btn-outline-secondary btn-sm" for="all">All</label>

        <template v-for="ft in filterTypes" :key="ft.type">
          <input type="radio" class="btn-check" name="filters" :id="`filter${ft.type}`" autocomplete="off" :value="ft.type" v-model="selectedFilterRef"/>
          <label class="btn btn-outline-secondary btn-sm" :for="`filter${ft.type}`">{{ ft.displayName }}</label>
        </template>
      </div>
    </div>
    <div v-if="contentItemsRef.length" class="content-area" :class="layoutRef.toLowerCase()">
      <div class="content-bounds">
        <button type="button" v-for="c in contentItemsRef" :key="c.id" class="btn btn-unstyled" @click="onContentClick(c.id!)">
          <img :src="c.galleryUrl"
            :alt="c.caption"
            :style="getContentStyle(c)"/>
        </button>
      </div>
    </div>
    <ContentViewer ref="contentViewerRef"/>
  </div>
</template>

<style lang="scss" scoped>
.content-explorer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.options-area {
  display: flex;
  gap: 1rem;
 
  .btn {
    min-width: 40px;
    //border-color: #ddd;
    border: none;
    border-radius: 0;
    padding: 0 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;

    img {
      width: 30px;
      opacity: 0.6;
    }

    &:hover {
      //border-color: #ccc;
      background-color: #f8f8f8;
    }

    &.disabled {
      opacity: 0.5;
    }
  }

  .btn-check:checked + .btn {
    //border-color: #aaa;
    background-color: #eee;
    color: #666;
  }
}

.content-area {
  width: 100%;

  .content-bounds {
    position: relative;
    width: 100%;
    height: 100%;
  }
 
  img {
    outline: 4px solid white;
    outline-offset: -1px; // hide anti-aliasing gap on rotated elements
    box-shadow: 2px 2px 8px 2px rgba(0, 0, 0, 0.2);
    background-color: white;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 2px 2px 8px 5px rgba(0, 0, 0, 0.25);
    }
  }

  &.pile {
    height: 400px;
    padding: 75px;

    img {
      position: absolute;
      max-width: 150px;
      max-height: 150px;
    }
  }

  &.grid {
    .content-bounds {
      display: grid;
      grid-template-columns: repeat(auto-fit, 120px);
      column-gap: 1rem;
      row-gap: 1rem;
      padding: 10px;
    }

    img {
      max-width: 120px;
      max-height: 120px;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, reactive, Ref, ref, watch } from 'vue'
import { Content, ItemType } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useContentTagStore } from '@/rd/ContentTagStore'
import { useContentStore } from '@/rd/ContentStore'
import { LoadMode } from '@/util/AsyncData'
import { hashCode, seededRandomFloat } from '@/util/RandomUtil'
import { useCommentStore } from '@/rd/CommentStore'
import ContentViewer from './ContentViewer.vue'

const props = defineProps<{
    personId?: string
}>()

defineExpose({
  getCount: () => contentItemsRef.value.length,
  hasSocialData: () => contentItemsRef.value.some(c => commentStore.getCommentCount(c.id!) > 0),
  showContent: onContentClick
})

enum LayoutType {
  Pile = 'pile',
  Grid = 'grid',
}

const filterTypes = [
  { type: ItemType.Person, displayName: 'Person' },
  // { type: ItemType.Family, displayName: 'Family' },
  // { type: ItemType.Assertion, displayName: 'Details' },
  // { type: ItemType.Place, displayName: 'Places' },
]

interface PilePosition {
  x: number
  y: number
  z: number
  rotation: number
}

const MIN_DISTANCE = 25 // minimum distance between items in % of container

const selectedLayoutRef = ref(LayoutType.Pile)
const selectedFilterRef = ref<ItemType>()
const contentItemsRef = ref([]) as Ref<Content[]>
const pileMap = reactive(new Map<string, PilePosition>())
const contentViewerRef = ref<InstanceType<typeof ContentViewer>>()

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const contentTagStore = useContentTagStore()
const contentStore = useContentStore()
const commentStore = useCommentStore()

const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const personContentTagsRef = computed(() => contentTagStore.getTagsForItems(ItemType.Person, viewPersonRef.value?.matchIds ?? [], LoadMode.EnsureLoaded))
const spouseFamiliesRef = computed(() => props.personId ? viewPersonStore.getLoadedSpouseFamilies(props.personId) : [])
//const familyContentTagsRef = computed(() => contentTagStore.getTagsForItems(ItemType.Family, [props.personId]))
const allContentTagsRef = computed(() => [...personContentTagsRef.value]) //, ...familyContentTagsRef.value])
const enablePileRef = computed(() => contentItemsRef.value.length > 4)
const layoutRef = computed(() => enablePileRef.value ? selectedLayoutRef.value : LayoutType.Grid)

type Point = { x: number, y: number }
const seedIndexRef = computed(() => hashCode(props.personId ?? '') % pileSeeds.length)

const pileSeeds = [
  // hand-picked values that produce a reasonably centered and distributed layout for 1-7 items
  1319026112,
  1319026114,
  1319026120,
  1319026126,
  1319026151,
]

watch([allContentTagsRef, seedIndexRef], async () => {
  const contentIds = new Set(allContentTagsRef.value.map(t => t.contentId!))
  const contentItems = await contentStore.getContentsAsync([...contentIds])

  // HACK
  const seed = pileSeeds[seedIndexRef.value]
  
  const xValues = seededRandomFloat(seed)
  const yValues = seededRandomFloat(seed + 1)
  const rotationValues = seededRandomFloat(seed + 2)

  pileMap.clear()
  const positions = [] as Point[]

  for (let i = 0; i < contentItems.length; i++) {
    const c = contentItems[i]
    if (!pileMap.has(c.id!)) {
      let x, y, attempts = 0
      do {
        x = xValues.next().value * 100 // %
        y = yValues.next().value * 100 // %
      } while (attempts++ < 10 && hasCollision(x, y, positions))
      if (attempts >= 20) {
        console.warn(`ContentExplorer: failed to place content without collision`)
      }
      positions.push({ x, y })

      const z = contentItems.length - i // first content item on top
      const rotation = rotationValues.next().value * 60 - 30 // +/- 30 degrees

      pileMap.set(c.id!, { x, y, z, rotation })
    }
  }

  contentItemsRef.value = contentItems
  commentStore.ensureSeeded(contentItems.map(c => c.id!))
})

function hasCollision(x: number, y: number, positions: Point[]) {
  for (const p of positions) {
    const dx = p.x - x
    const dy = p.y - y
    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance < MIN_DISTANCE) {
      return true // too close
    }
  }
  return false
}

function getContentStyle(c: Content) {
  if (layoutRef.value != LayoutType.Pile)
    return {}

  const position = pileMap.get(c.id!)!
  return {
    left: position.x + '%',
    top: position.y + '%',
    zIndex: position.z,
    transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
  }
}

function onContentClick(contentId: string) {
  const index = contentItemsRef.value.findIndex(item => item.id === contentId)
  contentViewerRef.value?.open(contentItemsRef.value, index)
}
</script>
